import i18n from '../translations/i18n';
import {
  DimmerStatus,
  IObjectAction,
  IObjectTrigger,
  SceneObjectType,
  IObjectCondition,
  ThermostatStatus,
  SceneDimmerState,
  SceneThermostatState,
  SceneLightState,
  SceneShutterState,
  SceneLockState,
  SceneFancoilState,
  SceneFancoilSpeedState,
  SceneTowelWarmerState,
  SceneStateComparer,
  SceneDimmerPowerState,
  SceneThermostatModalityState,
  SceneThermostatConditionState,
  ActionDTO,
  TriggerDTO,
  ConditionDTO,
  SceneBlindState,
  SceneSocketState,
  ScenePlayerState,
  IWeatherCondition,
  IWeatherTrigger,
  SceneWeatherModality,
  SceneWeatherForecastState,
  SceneWeatherCrepuscular,
  PlantDetailDTO,
  TemperatureType,
  SceneColoredLightsState,
  SceneLoadManagementState,
} from '../../../client-generator/generated-clients/plants';
import { SceneAlarmUtils } from './scene.alarm.utils';
import { SceneButtonUtils } from './scene.button.utils';

export class SceneUtils {
  private static $t = i18n.global.t;

  /**
   * takes an action object, a scene object type and returns the correct
   * description string
   *
   * @param action
   * @param sceneObjectType
   * @returns string
   */
  static getObjectDescriptionAction(
    action: IObjectAction,
    sceneObjectType: SceneObjectType,
    sceneId: string,
    plant: PlantDetailDTO | null
  ): string {
    let description!: string;
    switch (sceneObjectType) {
      case SceneObjectType.GeneralCommandsLights:
      case SceneObjectType.GroupOfLights:
      case SceneObjectType.Light:
        // TODO: check if lightState.ToString() is the same of action.actionParams.sceneLightState
        description = `${this.$t('AppResource.Turn')} ${this.$t(
          this.enumValToStr(SceneLightState, action.sceneLightState)
        )}`;
        break;

      case SceneObjectType.Dimmer:
      case SceneObjectType.DimmerDmx:
        description =
          action.dimmerStatus == DimmerStatus.Dimmer
            ? `${this.$t('AppResource.Dimmer')} ${action.dimmerPercentage}%`
            : `${this.$t('AppResource.Turn')} ${this.$t(
                this.enumValToStr(DimmerStatus, action.dimmerStatus)
              ).toLowerCase()}`;
        break;

      case SceneObjectType.GeneralCommandsShutters:
      case SceneObjectType.GroupOfShutters:
      case SceneObjectType.Shutter:
        if (action.isShutterPositionManagement) {
          description = `${this.$t('AppResource.Position')} ${
            action.sceneShutterPosition
          }%`;
        } else {
          description = this.$t(
            this.enumValToStr(SceneShutterState, action.sceneShutterState)
          );
        }
        break;

      case SceneObjectType.Lock:
        description = this.$t(
          this.enumValToStr(SceneLockState, action.sceneLockState)
        );
        break;

      case SceneObjectType.Thermostat:
        description =
          action.thermostatStatus == ThermostatStatus.Off
            ? this.$t(
                this.enumValToStr(ThermostatStatus, action.thermostatStatus)
              )
            : `${this.$t(
                this.enumValToStr(ThermostatStatus, action.thermostatStatus)
              )} ${action.thermostatTriggerTemperature} ${this.$t(
                'AppResource.Celsius'
              )}`;
        break;

      case SceneObjectType.Fancoil:
        description = `${this.$t('AppResource.Turn')} ${this.$t(
          this.enumValToStr(SceneFancoilState, action.sceneFancoilState)
        ).toLowerCase()} - ${this.$t(
          'AppResource.FanSpeed'
        ).toLowerCase()} ${this.enumValToStr(
          SceneFancoilSpeedState,
          action.fancoilSpeed
        )}`;
        break;

      case SceneObjectType.TowelWarmer:
        description = `${this.$t('AppResource.Turn')} ${this.$t(
          this.enumValToStr(SceneTowelWarmerState, action.sceneTowelWarmerState)
        ).toLowerCase()}`;
        break;

      case SceneObjectType.Blind:
        description = `${this.$t(
          this.enumValToStr(SceneBlindState, action.sceneBlindState)
        ).toLowerCase()}`;
        break;

      case SceneObjectType.Socket:
        description = `${this.$t(
          this.enumValToStr(SceneSocketState, action.sceneSocketState)
        ).toLowerCase()}`;
        break;

      case SceneObjectType.PushNotificationAction:
        description = (action as any).message;
        break;

      case SceneObjectType.Email:
        description = `${this.$t('AppResource.MessageTo')} ${
          (action as any).emailAddress
        }`;
        break;

      case SceneObjectType.Player:
        description = `${this.$t('AppResource.Turn')} ${this.$t(
          this.enumValToStr(ScenePlayerState, action.scenePlayerState)
        )}`;

        if (action.scenePlayerState == ScenePlayerState.Play) {
          description += ` - ${this.$t('AppResource.Volume')} ${
            action.playerVolume
          } `;
        }
        break;
      case SceneObjectType.ColoredLights:
        {
          const hexColor = this.hueToHex(action.coloredLightsColor!);
          description = `${this.$t(
            this.enumValToStr(
              SceneColoredLightsState,
              action.sceneColoredLightsState
            )
          )}`;
          if (action.sceneColoredLightsState == SceneColoredLightsState.On) {
            description += ` - ${this.$t('AppResource.IntensityLower')} ${
              action.coloredLightsIntensity
            } - ${this.$t('AppResource.TemperatureLower')} ${
              action.coloredLightsTemperature
            } - ${this.$t('AppResource.ColorLower')} ${hexColor}`;
          }
        }
        break;
      case SceneObjectType.Alarm:
        description = SceneAlarmUtils.getAlarmObjectDescription(action, plant);
        break;
      case SceneObjectType.ActuactorLoadControl:
        description = `${this.$t(
          this.enumValToStr(SceneLoadManagementState, action.sceneLoadManagementStatus)
        )}`;
        break;
      default:
        break;
    }

    return description;
  }

  private static hueToHex(h: number) {
    h /= 360;
    let s = 1;
    let v = 1;
    let r = 0;
    let g = 0;
    let b = 0;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);

    switch (i % 6) {
      case 0:
        (r = v), (g = t), (b = p);
        break;
      case 1:
        (r = q), (g = v), (b = p);
        break;
      case 2:
        (r = p), (g = v), (b = t);
        break;
      case 3:
        (r = p), (g = q), (b = v);
        break;
      case 4:
        (r = t), (g = p), (b = v);
        break;
      case 5:
        (r = v), (g = p), (b = q);
        break;
    }
    return `#${(
      (1 << 24) +
      (Math.round(r * 255) << 16) +
      ((g * 255) << 8) +
      b * 255
    )
      .toString(16)
      .slice(1)}`.toUpperCase();
  }

  /**
   * takes a Trigger or Condition object, a scene object type and returns the correct
   * description string
   *
   * @param toc TriggerOrCondition
   * @param sceneObjectType
   * @returns string
   */
  public static getObjectDescriptionTriggerCondition(
    toc: IObjectTrigger | IObjectCondition,
    sceneObjectType: SceneObjectType,
    sceneId: string,
    plant: PlantDetailDTO | null = null
  ): string {
    var description!: string;
    switch (sceneObjectType) {
      case SceneObjectType.Light:
        description = `${this.$t('AppResource.EqualTo')} ${this.$t(
          this.enumValToStr(SceneLightState, toc.lightState)
        ).toLowerCase()}`;
        break;
      case SceneObjectType.Socket:
        {
          description = `${this.$t('AppResource.EqualTo')} ${this.$t(
            this.enumValToStr(SceneSocketState, toc.socketState)
          )?.toLowerCase()} `;
        }
        break;
      case SceneObjectType.Dimmer:
      case SceneObjectType.DimmerDmx:
        description = `${this.$t(
          this.enumValToStr(SceneDimmerState, toc.dimmerState)
        )?.toLowerCase()} ${this.$t(
          this.enumValToStr(SceneStateComparer, toc.stateComparer)
        )?.toLowerCase()}`;
        description =
          toc.dimmerState == SceneDimmerState.Power
            ? `${description} ${this.$t(
                this.enumValToStr(SceneDimmerPowerState, toc.dimmerPowerState)
              )?.toLowerCase()}`
            : `${description} ${toc.dimmerPercent}`;
        break;

      case SceneObjectType.Shutter:
        description = `${this.$t(
          this.enumValToStr(SceneStateComparer, toc.stateComparer)
        )?.toLowerCase()} ${this.$t('AppResource.To')} ${this.$t(
          this.enumValToStr(SceneShutterState, toc.shutterState)
        )?.toLowerCase()}`;
        break;

      case SceneObjectType.Blind:
        {
          description = `${this.$t('AppResource.To')} ${this.$t(
            this.enumValToStr(SceneBlindState, toc.blindState)
          )?.toLowerCase()}`;
        }
        break;
      case SceneObjectType.Lock:
        description = `${this.$t('AppResource.EqualTo')} ${this.$t(
          this.enumValToStr(SceneLockState, toc.lockState)
        )?.toLowerCase()}`;
        break;

      case SceneObjectType.Thermostat:
        description = SceneUtils.getThermostatDescription(description, toc);
        break;

      case SceneObjectType.Fancoil:
        description = `${this.$t('AppResource.EqualTo')} ${this.$t(
          this.enumValToStr(SceneFancoilState, toc.fancoilState)
        )?.toLowerCase()} - ${this.$t(
          'AppResource.FanSpeed'
        ).toLowerCase()} ${this.$t(
          this.enumValToStr(SceneFancoilSpeedState, toc.fancoilSpeedState)
        )}`;
        break;

      case SceneObjectType.TowelWarmer:
        description = `${this.$t('AppResource.EqualTo')} ${this.$t(
          this.enumValToStr(SceneTowelWarmerState, toc.towelWarmerState)
        )?.toLowerCase()}`;
        break;

      case SceneObjectType.Weather:
        description = this.getWeatherDescription(toc, plant);
        break;
      case SceneObjectType.Xmeter:
        description = `${this.$t(
          this.enumValToStr(SceneStateComparer, toc.stateComparer)
        )} ${toc.energyMeterKw} kw`;
        break;
      case SceneObjectType.Alarm:
        description = SceneAlarmUtils.getAlarmObjectDescription(toc, plant);
        break;
      case SceneObjectType.Command:
          description = SceneButtonUtils.getButtonTriggerDescription(toc, sceneId);
        break;
      default:
        break;
    }

    return description;
  }

  public static getObjectDescription(
    obj: ActionDTO | TriggerDTO | ConditionDTO,
    sceneId: string,
    plant: PlantDetailDTO | null = null
  ): string {
    const empty = '';
    if (obj.objectName === null) return empty;
    if (obj instanceof ActionDTO) {
      if (!obj.actionParams) return empty;

      return this.getObjectDescriptionAction(
        obj.actionParams,
        obj.sceneObjectType!,
        sceneId,
        plant
      );
    }

    if (obj instanceof TriggerDTO) {
      if (!obj.triggerParams) return empty;
      return this.getObjectDescriptionTriggerCondition(
        obj.triggerParams,
        obj.sceneObjectType!,
        sceneId,
        plant
      );
    }

    if (obj instanceof ConditionDTO) {
      if (!obj.conditionParams) return empty;
      return this.getObjectDescriptionTriggerCondition(
        obj.conditionParams,
        obj.sceneObjectType!,
        sceneId,
        plant
      );
    }

    return empty;
  }

  private static getWeatherDescription(
    toc: IWeatherCondition | IWeatherTrigger,
    plant: PlantDetailDTO | null = null
  ) {
    let description = '';
    const temperatureType =
      plant?.mxServer?.temperatureType == TemperatureType.Fahrenheit
        ? 'F'
        : 'C';
    const weatherStComparerLocal = this.$t(
      this.enumValToStr(SceneStateComparer, toc.weatherStateComparer)
    )?.toLowerCase();
    const sceneWeatherModality = this.$t(
      this.enumValToStr(SceneWeatherModality, toc.sceneWeatherModality)
    );
    switch (toc.sceneWeatherModality) {
      case SceneWeatherModality.Temperature:
        description = `${sceneWeatherModality} ${weatherStComparerLocal} ${toc.weatherTemperature}°${temperatureType}`;
        break;

      case SceneWeatherModality.Wind:
        description = `${sceneWeatherModality} ${weatherStComparerLocal} ${toc.weatherWindSpeed} KM/H`;
        break;

      case SceneWeatherModality.Humidity:
        description = `${sceneWeatherModality} ${weatherStComparerLocal} ${toc.humidityPercentage}%`;
        break;

      case SceneWeatherModality.WeatherForecast:
        description = `${sceneWeatherModality} ${weatherStComparerLocal} ${this.$t(
          this.enumValToStr(
            SceneWeatherForecastState,
            toc.sceneWeatherForecastState
          )
        )}`;
        break;

      case SceneWeatherModality.Crepuscular:
        description = `${sceneWeatherModality} ${this.$t(
          'AppResource.EqualTo'
        )}`;
        if (toc.sceneWeatherCrepuscular == SceneWeatherCrepuscular.Sunrise) {
          description = `${description} ${this.$t('AppResource.Day')}`;
        } else {
          description = `${description} ${this.$t('AppResource.Night')}`;
        }
        break;
    }
    return description;
  }

  private static getThermostatDescription(
    description: string,
    toc: IObjectTrigger | IObjectCondition
  ) {
    description = `${this.$t(
      this.enumValToStr(SceneThermostatState, toc.thermostatState)
    )?.toLowerCase()} ${this.$t(
      this.enumValToStr(SceneStateComparer, toc.stateComparer)
    )?.toLowerCase()}`;
    switch (toc.thermostatState) {
      case SceneThermostatState.Modality:
        description = `${description} ${this.$t(
          this.enumValToStr(
            SceneThermostatModalityState,
            toc.thermostatModalityState
          )
        )?.toLowerCase()}`;
        break;

      case SceneThermostatState.Condition:
        description = `${description} ${this.$t(
          this.enumValToStr(
            SceneThermostatConditionState,
            toc.thermostatConditionState
          )
        )?.toLowerCase()}`;
        break;

      case SceneThermostatState.Temperature:
      case SceneThermostatState.DesiredTemperature:
        description = `${description} ${toc.thermostatTemperature} ${this.$t(
          'AppResource.Celsius'
        )}`;
        break;
      case SceneThermostatState.Humidity:
        description = `${description} ${toc.thermostatHumidity}%`;
        break;
      default:
        break;
    }
    return description;
  }

  public static getWeekDayShortName(dayValue: number): string {
    switch (dayValue) {
      case 0:
        return this.$t('AppResource.FirstWeekdayInitial');
      case 1:
        return this.$t('AppResource.SecondWeekdayInitial');
      case 2:
        return this.$t('AppResource.ThirdWeekdayInitial');
      case 3:
        return this.$t('AppResource.FourthWeekdayInitial');
      case 4:
        return this.$t('AppResource.FifthWeekdayInitial');
      case 5:
        return this.$t('AppResource.SixthWeekdayInitial');
      case 6:
        return this.$t('AppResource.SeventhWeekdayInitial');
      default:
        return '';
    }
  }

  private static enumValToStr(targetEnum: any, numberVal: any): string {
    if (typeof numberVal !== 'number')
      throw new Error(
        `Invalid numberVal given to \`enumValToStr\`: ${numberVal}`
      );

    if (SceneStateComparer == targetEnum) {
      switch (targetEnum[numberVal]) {
        case 'Lower':
          return `AppResource.LowerThan`;
        case 'Higher':
          return `AppResource.GreaterThan`;
        case 'Equal':
          return 'AppResource.EqualToFirstUpper';
        case 'Different':
          return 'AppResource.DifferentFromFirstUpper';
        default:
          return `AppResource.${targetEnum[numberVal]}`;
      }
    }
    return `AppResource.${targetEnum[numberVal]}`;
  }
}
